// Game Types - Updated to match backend game registry
export const GAME_TYPES = {
  TIC_TAC_TOE: 'tic-tac-toe',
  NUMBER_GUESS: 'number-guess',
  ROCK_PAPER_SCISSORS: 'rock-paper-scissors',
  HANGMAN: 'hangman',
  TRIVIA: 'trivia',
  CHESS: 'chess',
  BATTLESHIP: 'battleship',
  BATTLE_GRID: 'battle-grid'
};

// Game Categories
export const GAME_CATEGORIES = {
  CLASSIC: 'classic',
  PUZZLE: 'puzzle',
  STRATEGY: 'strategy',
  WORD: 'word',
  MATH: 'math',
  CARD: 'card'
};

// Subscription Tiers
export const SUBSCRIPTION_TIERS = {
  FREE: 'free',
  PRO: 'pro',
  ENTERPRISE: 'enterprise'
};

// Legacy game configs - kept for backward compatibility
// New games should be fetched from the backend API
export const GAME_CONFIGS = {
  [GAME_TYPES.TIC_TAC_TOE]: {
    name: 'Tic-Tac-Toe',
    emoji: '❌⭕',
    description: 'Classic 3x3 grid game with AI opponent',
    category: GAME_CATEGORIES.CLASSIC,
    subscriptionTier: SUBSCRIPTION_TIERS.FREE,
    minPlayers: 1,
    maxPlayers: 2,
    estimatedDuration: 300, // 5 minutes
    difficulty: ['easy', 'medium', 'hard']
  },
  [GAME_TYPES.NUMBER_GUESS]: {
    name: 'Number Guess',
    emoji: '🔢',
    description: 'Guess the number with hints',
    category: GAME_CATEGORIES.PUZZLE,
    subscriptionTier: SUBSCRIPTION_TIERS.FREE,
    minPlayers: 1,
    maxPlayers: 1,
    estimatedDuration: 300, // 5 minutes
    difficulty: ['easy', 'medium', 'hard', 'extreme']
  },
  [GAME_TYPES.ROCK_PAPER_SCISSORS]: {
    name: 'Rock Paper Scissors',
    emoji: '🪨📄✂️',
    description: 'Classic hand game with multiple rounds',
    category: GAME_CATEGORIES.CLASSIC,
    subscriptionTier: SUBSCRIPTION_TIERS.PRO,
    minPlayers: 1,
    maxPlayers: 2,
    estimatedDuration: 180, // 3 minutes
    difficulty: ['easy', 'medium', 'hard', 'extreme']
  },
  [GAME_TYPES.HANGMAN]: {
    name: 'Hangman',
    emoji: '🎯',
    description: 'Guess the word before the hangman is drawn',
    category: GAME_CATEGORIES.WORD,
    subscriptionTier: SUBSCRIPTION_TIERS.PRO,
    minPlayers: 1,
    maxPlayers: 1,
    estimatedDuration: 600, // 10 minutes
    difficulty: ['easy', 'medium', 'hard']
  },
  [GAME_TYPES.TRIVIA]: {
    name: 'Trivia Challenge',
    emoji: '🧠',
    description: 'Test your knowledge with various trivia questions',
    category: GAME_CATEGORIES.PUZZLE,
    subscriptionTier: SUBSCRIPTION_TIERS.PRO,
    minPlayers: 1,
    maxPlayers: 4,
    estimatedDuration: 900, // 15 minutes
    difficulty: ['easy', 'medium', 'hard']
  },
  [GAME_TYPES.CHESS]: {
    name: 'Chess',
    emoji: '♟️',
    description: 'Classic strategy board game',
    category: GAME_CATEGORIES.STRATEGY,
    subscriptionTier: SUBSCRIPTION_TIERS.PRO,
    minPlayers: 1,
    maxPlayers: 2,
    estimatedDuration: 1800, // 30 minutes
    difficulty: ['easy', 'medium', 'hard']
  },
  [GAME_TYPES.BATTLESHIP]: {
    name: 'Battleship',
    emoji: '🚢',
    description: 'Naval warfare strategy game',
    category: GAME_CATEGORIES.STRATEGY,
    subscriptionTier: SUBSCRIPTION_TIERS.ENTERPRISE,
    minPlayers: 1,
    maxPlayers: 2,
    estimatedDuration: 1200, // 20 minutes
    difficulty: ['medium', 'hard']
  },
  [GAME_TYPES.BATTLE_GRID]: {
    name: 'Battle Grid RPG',
    emoji: '⚔️',
    description: 'Tactical 5x5 grid battle with heroes and strategy',
    category: GAME_CATEGORIES.STRATEGY,
    subscriptionTier: SUBSCRIPTION_TIERS.PRO,
    minPlayers: 2,
    maxPlayers: 2,
    estimatedDuration: 600, // 10 minutes
    difficulty: ['medium', 'hard']
  }
};

export const GAME_STATUS = {
  WAITING: 'waiting',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  ABANDONED: 'abandoned'
};

export const GAME_DIFFICULTY = {
  EASY: 'easy',
  MEDIUM: 'medium',
  HARD: 'hard',
  EXTREME: 'extreme'
};

// Utility functions for game management
export const getGameConfig = (gameType) => {
  return GAME_CONFIGS[gameType] || null;
};

export const getGameEmoji = (gameType) => {
  return GAME_CONFIGS[gameType]?.emoji || '🎮';
};

export const getGameName = (gameType) => {
  return GAME_CONFIGS[gameType]?.name || gameType;
};

export const formatGameType = (gameType) => {
  return gameType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Subscription tier utilities
export const getSubscriptionTierName = (tier) => {
  switch (tier) {
    case SUBSCRIPTION_TIERS.FREE:
      return 'Free';
    case SUBSCRIPTION_TIERS.PRO:
      return 'Pro';
    case SUBSCRIPTION_TIERS.ENTERPRISE:
      return 'Enterprise';
    default:
      return 'Unknown';
  }
};

export const getSubscriptionTierColor = (tier) => {
  switch (tier) {
    case SUBSCRIPTION_TIERS.FREE:
      return 'text-gray-400';
    case SUBSCRIPTION_TIERS.PRO:
      return 'text-blue-400';
    case SUBSCRIPTION_TIERS.ENTERPRISE:
      return 'text-purple-400';
    default:
      return 'text-gray-400';
  }
};

export const getSubscriptionTierBadgeColor = (tier) => {
  switch (tier) {
    case SUBSCRIPTION_TIERS.FREE:
      return 'badge-secondary';
    case SUBSCRIPTION_TIERS.PRO:
      return 'badge-primary';
    case SUBSCRIPTION_TIERS.ENTERPRISE:
      return 'badge-accent';
    default:
      return 'badge-secondary';
  }
};

// Check if user has access to a game based on subscription
export const hasGameAccess = (userSubscription, gameTier) => {
  const tierHierarchy = {
    [SUBSCRIPTION_TIERS.FREE]: 0,
    [SUBSCRIPTION_TIERS.PRO]: 1,
    [SUBSCRIPTION_TIERS.ENTERPRISE]: 2
  };
  
  const userLevel = tierHierarchy[userSubscription] || 0;
  const gameLevel = tierHierarchy[gameTier] || 0;
  
  return userLevel >= gameLevel;
};

// Get games by subscription tier
export const getGamesByTier = (tier) => {
  return Object.values(GAME_CONFIGS).filter(game => game.subscriptionTier === tier);
};

// Get games by category
export const getGamesByCategory = (category) => {
  return Object.values(GAME_CONFIGS).filter(game => game.category === category);
};
